import type { RecordingId } from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import { useCallback, useContext } from "react";
import { AudioPlayerContext } from "../../components/audioPlayer/context.js";
import type { CallPosition } from "./callNavigationHelpers.js";
import { findFragmentIndexForKeyMoment } from "./gradingHelpers.js";

interface UseCallNavigationActionsOptions {
  recordings: readonly AccountGradingRecording[];
  onCallPositionRequest: (position: CallPosition) => void;
}

/** Selects call audio and transcript positions without changing playback state. */
export function useCallNavigationActions(
  options: UseCallNavigationActionsOptions,
) {
  const { recordings, onCallPositionRequest } = options;
  const audioPlayer = useContext(AudioPlayerContext);
  const selectPosition = useCallback(
    (position: CallPosition) => {
      if (audioPlayer.nativeAudio && !audioPlayer.nativeAudio.paused) {
        audioPlayer.play(position.recordingId, position.timestamp / 1000);
      } else {
        audioPlayer.seek(position.recordingId, position.timestamp / 1000);
      }
      onCallPositionRequest(position);
    },
    [audioPlayer, onCallPositionRequest],
  );

  const selectCall = useCallback(
    (recordingId: RecordingId) => {
      const firstFragment = recordings.find(
        (recording) => recording.id === recordingId,
      )?.transcript?.fragments[0];
      selectPosition(
        firstFragment
          ? {
              recordingId,
              timestamp: firstFragment.start_time,
              fragmentIndex: 0,
            }
          : { recordingId, timestamp: 0 },
      );
    },
    [recordings, selectPosition],
  );

  const selectSegment = useCallback(
    (position: CallPosition) => {
      const fragments = recordings.find(
        (recording) => recording.id === position.recordingId,
      )?.transcript?.fragments;
      if (!fragments) {
        selectPosition(position);
        return;
      }
      const fragmentIndex = findFragmentIndexForKeyMoment(
        fragments,
        position.timestamp,
      );
      const fragment =
        fragmentIndex === undefined ? undefined : fragments[fragmentIndex];
      selectPosition(
        fragment
          ? { ...position, timestamp: fragment.start_time, fragmentIndex }
          : position,
      );
    },
    [recordings, selectPosition],
  );

  return { selectCall, selectSegment };
}
